import React from 'react';
import { ArrowRightIcon, EyeIcon, PawPrintIcon, RadioIcon, RotateCcwIcon, WavesIcon } from 'lucide-react';
import { useDemo } from '../../contexts/DemoContext';
import { ScreenContainer } from '../story/ScreenContainer';
import { ScreenHeading } from '../story/ScreenHeading';
import { DialogueCard } from '../story/DialogueCard';
import { DialogueList } from '../story/DialogueList';
import { ChoiceCard } from '../story/ChoiceCard';
import { ActionRow } from '../story/ActionRow';
import { Button } from '../story/Button';
import { SceneIllustration } from '../art/SceneIllustration';
import { SavannaScene } from '../art/SavannaScene';
import { storyActions } from '../../data/savanna';
import { AMARA_MENTOR } from '../../data/speakers';
import type { ActionId } from '../../types/demo';
const ICONS: Record<ActionId, React.ReactNode> = {
  watch: <EyeIcon size={20} aria-hidden="true" data-id="element-53" data-mp="evxlousp5ux0" />,
  signal: <RadioIcon size={20} aria-hidden="true" data-id="element-54" data-mp="av3yk3n2wk56" />,
  sidePath: <WavesIcon size={20} aria-hidden="true" data-id="element-55" data-mp="avnejet64ygk" />,
  panther: <PawPrintIcon size={20} aria-hidden="true" data-id="element-56" data-mp="3fset4xb7bsl0" />
};
const BASE_SCENE = 'Monkey waits at the water’s edge. Panther is low near the grass. Fog sits over the side-water under the roots. Amara stands near the acacia.';
export function ActionScreen() {
  const {
    state,
    dispatch,
    world,
    announce
  } = useDemo();
  const chosen = storyActions.find(item => item.id === state.activeAction) ?? null;
  const description = chosen ? chosen.sceneDescription : BASE_SCENE;
  return <ScreenContainer data-id="element-57" data-mp="7d3djseh9ji7">
      <ScreenHeading eyebrow="Choose a next step" focusKey={chosen?.id ?? 'choose'} data-id="element-58" data-mp="7dmwncfv6dxt">
        {chosen ? chosen.label : 'One true thing. Maybe two.'}
      </ScreenHeading>
      <div className="mt-6 grid gap-8 lg:grid-cols-[1.35fr_1fr] lg:items-start" data-id="element-59" data-mp="3pl2ix83rkwa8">
        <SceneIllustration description={description} quietTitle="Watering hole" data-id="element-60" data-mp="3tmeervd8edqv">
          <SavannaScene world={world} label={description} data-id="element-61" data-mp="2e1zvxf5bk055" />
        </SceneIllustration>
        <div className="flex flex-col gap-4" data-id="element-62" data-mp="2i5xv1pun9ay7">
          {!chosen ? <>
              <DialogueCard line={{
            kind: 'speech',
            speaker: AMARA_MENTOR,
            text: 'Okay. One true thing. Maybe two. That is not nothing.'
          }} data-id="element-63" data-mp="ole259lpfvk6" />
              <p data-id="element-64" data-mp="ewcpntedbway">Pick one route for Amara. You can try another one after.</p>
            </> : <div key={chosen.id} className="anim-enter flex flex-col gap-4" data-id="element-65" data-mp="9y2rd9xckfl4">
              <DialogueList lines={chosen.lines} data-id="element-66" data-mp="5vy0giya9x26" />
              <div className="rounded-2xl border border-line bg-paper p-5 hc-strong" data-id="element-67" data-mp="5wpmb222wgf9">
                <p className="text-sm font-bold text-teal" data-id="element-68" data-mp="vtispln2wad">What changed</p>
                <p className="mt-1" data-id="element-69" data-mp="3s2w7nae08qhf">{chosen.feedback}</p>
              </div>
              {chosen.note && <p className="text-sm text-ink/80" data-id="element-70" data-mp="3n4b87d6kuau1">{chosen.note}</p>}
            </div>}
        </div>
      </div>

      {!chosen && <ul className="mt-8 grid gap-3 sm:grid-cols-2" data-id="element-71" data-mp="1qahvkry56g8x">
          {storyActions.map(item => <li key={item.id} data-id="element-72" data-mp="1ldztjzu74w0a">
              <ChoiceCard title={item.label} summary={item.summary} note={item.note} icon={ICONS[item.id]} onClick={() => {
          dispatch({
            type: 'CHOOSE_ACTION',
            action: item.id
          });
          announce(item.feedback);
        }} data-id="element-73" data-mp="1lalnagiew2nk" />
            </li>)}
        </ul>}

      <ActionRow onBack={() => dispatch({
      type: 'BACK'
    })} data-id="element-74" data-mp="27n7v9f7ktbww">
        {chosen && <>
            <Button variant="secondary" onClick={() => {
          dispatch({
            type: 'REPLAY_ROUTE'
          });
          announce('Route cleared. Choose a next step.');
        }} icon={<RotateCcwIcon size={18} aria-hidden="true" data-id="element-76" data-mp="1oucf9nyb28ej" />} data-id="element-75" data-mp="1awxnwdohrqjx">
              Try another route
            </Button>
            <Button onClick={() => dispatch({
          type: 'GO',
          screen: 'witness'
        })} iconAfter={<ArrowRightIcon size={18} aria-hidden="true" data-id="element-78" data-mp="308q16n9i0drx" />} data-id="element-77" data-mp="2vbhhgi8pc1ok">
              See the next clue
            </Button>
          </>}
      </ActionRow>
    </ScreenContainer>;
}